import { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import { isPreviewable } from '../../services/files/fileUtils';
import { getDownloadUrl } from '../../services/files/filesService';
import type { DossierFile } from '../../services/files/filesService';

interface Props {
  file: DossierFile;
  onClose: () => void;
}

export function FilePreviewModal({ file, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const kind = isPreviewable(file.mime, file.filename);

  useEffect(() => {
    let alive = true;
    getDownloadUrl(file.id)
      .then((u) => { if (alive) setUrl(u); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'Erreur'); });
    return () => { alive = false; };
  }, [file.id]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-4xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-2 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold truncate">{file.filename}</h2>
          <div className="flex items-center gap-2">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded hover:bg-neutral-100"
                aria-label="Télécharger"
              >
                <Download className="w-4 h-4 text-neutral-600" />
              </a>
            )}
            <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-neutral-100">
              <X className="w-4 h-4 text-neutral-600" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-auto bg-neutral-100 flex items-center justify-center">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!url && !error && <p className="text-sm text-neutral-500">Chargement…</p>}
          {url && kind === 'pdf' && (
            <iframe src={url} className="w-full h-full" title={file.filename} />
          )}
          {url && kind === 'image' && (
            <img src={url} alt={file.filename} className="max-w-full max-h-full object-contain" />
          )}
          {url && kind === null && (
            <p className="text-sm text-neutral-500">Aperçu indisponible pour ce type de fichier.</p>
          )}
        </div>
      </div>
    </div>
  );
}
